import React, { useEffect, useMemo } from "react";
import { Card, Row, Col, Input, Radio } from "antd";
import NumberFormat from "react-number-format";
import RadioButton from "../../../../components/utils/radioButton";

const DryMatterConsumeCard = ({
  herds = [],
  values,
  setFieldValue,
  errors,
  submitCount,
  translation,
}) => {
  const measurementType =
    values.dryMatterConsumeContract?.measurementType || null;
  const reportType = values.dryMatterConsumeContract?.reportType || null;
  const uniqueData = values.dryMatterConsumeContract?.uniqueData || {
    averageOffer: null,
    numberOfDays: null,
    costPerKilogramDryMatter: null,
  };

  useEffect(() => {
    // Sincroniza dados de herdsWithData para herds na inicialização (quando carrega dados existentes)
    if (
      values.dryMatterConsumeContract?.herdsWithData &&
      reportType === "cjto" &&
      values.herds?.length > 0
    ) {
      const hasDataInHerds = values.herds.some(
        (h) => h.averageOffer !== null || h.numberOfDays !== null || h.costPerKilogramDryMatter !== null
      );

      // Se herds não tem os dados mas herdsWithData tem, copia para herds
      if (!hasDataInHerds) {
        const updatedHerds = (values.herds || []).map((herd) => {
          const herdData = values.dryMatterConsumeContract.herdsWithData.find(
            (h) => h.id === herd.id
          );
          return {
            ...herd,
            averageOffer: herdData?.averageOffer ?? null,
            numberOfDays: herdData?.numberOfDays ?? null,
            costPerKilogramDryMatter: herdData?.costPerKilogramDryMatter ?? null,
          };
        });
        setFieldValue("herds", updatedHerds);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Sincroniza os dados dos herds quando a lista de herds (prop) muda
    // Garante que novos herds tenham os campos necessários
    const herdsIds = (herds || []).map((h) => h.id).sort().join(",");
    const valuesHerdsIds = (values.herds || []).map((h) => h.id).sort().join(",");

    // Só atualiza se a lista de IDs mudou (herds adicionados ou removidos)
    if (herdsIds !== valuesHerdsIds && herdsIds) {
      const updatedHerds = (herds || []).map((herd) => {
        const existingHerd = values.herds?.find((h) => h.id === herd.id);
        // Preserva os dados existentes ou inicializa como null
        return {
          ...herd,
          averageOffer: existingHerd?.averageOffer ?? null,
          numberOfDays: existingHerd?.numberOfDays ?? null,
          costPerKilogramDryMatter: existingHerd?.costPerKilogramDryMatter ?? null,
        };
      });
      setFieldValue("herds", updatedHerds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [herds]);

  const updateDryMatterConsumeContract = (updates) => {
    const currentContract = values.dryMatterConsumeContract || {};
    const newReportType = updates.reportType ?? reportType;
    const newMeasurementType = updates.measurementType ?? measurementType;

    // Gera herdsWithData a partir de values.herds quando reportType é "cjto"
    const herdsWithData =
      newReportType === "cjto"
        ? (values.herds || []).map((h) => ({
            id: h.id,
            averageOffer: h.averageOffer,
            numberOfDays: h.numberOfDays,
            costPerKilogramDryMatter: h.costPerKilogramDryMatter,
          }))
        : null;

    setFieldValue("dryMatterConsumeContract", {
      ...currentContract,
      ...updates,
      measurementType: newMeasurementType,
      reportType: newReportType,
      herdsWithData,
      uniqueData:
        newReportType === "unico"
          ? (updates.uniqueData ?? uniqueData)
          : (updates.uniqueData ?? null),
    });
  };

  const handleMeasurementTypeChange = (e) => {
    updateDryMatterConsumeContract({
      measurementType: e.target.value,
    });
  };

  const handleReportTypeChange = (e) => {
    const newReportType = e.target.value;
    if (newReportType === "unico") {
      const clearedHerds = (values.herds || []).map((h) => ({
        ...h,
        averageOffer: null,
        numberOfDays: null,
        costPerKilogramDryMatter: null,
      }));
      setFieldValue("herds", clearedHerds);
    }
    if (newReportType === "cjto") {
      updateDryMatterConsumeContract({
        reportType: newReportType,
        uniqueData: {
          averageOffer: null,
          numberOfDays: null,
          costPerKilogramDryMatter: null,
        },
      });
      return;
    }
    updateDryMatterConsumeContract({ reportType: newReportType });
  };

  const handleHerdDataChange = (herdId, field, value) => {
    const updatedHerds = (values.herds || []).map((h) =>
      h.id === herdId ? { ...h, [field]: value } : h
    );
    setFieldValue("herds", updatedHerds);

    // Atualiza herdsWithData no dryMatterConsumeContract se reportType for "cjto"
    if (reportType === "cjto") {
      const herdsWithData = updatedHerds.map((h) => ({
        id: h.id,
        averageOffer: h.averageOffer,
        numberOfDays: h.numberOfDays,
        costPerKilogramDryMatter: h.costPerKilogramDryMatter,
      }));
      updateDryMatterConsumeContract({ herdsWithData });
    }
  };

  const handleUniqueDataChange = (field, value) => {
    const updatedUniqueData = { ...uniqueData, [field]: value };
    updateDryMatterConsumeContract({ uniqueData: updatedUniqueData });
  };

  const showAverageOffer = useMemo(() => {
    return reportType === "cjto";
  }, [reportType]);

  const showNumberOfDays = useMemo(() => {
    return reportType === "cjto" &&  measurementType === "pre_fixado";
  }, [measurementType, reportType]);

  const showUniqueNumberOfDays = useMemo(() => {
    return reportType === "unico" && measurementType === "pre_fixado";
  }, [measurementType, reportType]);

  const showCostPerKilogramDryMatter = useMemo(() => {
    return (
      (reportType === "cjto" && measurementType === "fornercimento") ||
      (reportType === "cjto" && measurementType === "pre_fixado")
    );
  }, [reportType, measurementType]);

  const showUniqueRow = () => {
    return reportType === "unico";
  };

  const formatCostForDisplay = (v) => {
    if (v === null || v === undefined || v === "") return "";
    if (typeof v === "string") return v;
    return Number(v).toFixed(2).replace(".", ",");
  };

  return (
    <Card
      style={{
        marginTop: 16,
        borderRadius: 7,
        background: "#fff",
        boxShadow: "0px 0px 10px #d8d8d8",
      }}
    >
      <Row type="flex" gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} md={8} lg={8} xl={12} xxl={12}>
          <Row type="flex" gutter={16} style={{ marginBottom: 16 }}>
            <Col span={12}>
              <Row>
                <label
                  className={
                    errors.dryMatterConsumeContract?.measurementType &&
                    submitCount > 0
                      ? "error"
                      : ""
                  }
                >
                  {translation.boitelContractsPage.form.dryMatterConsumeContract
                    .measurement}
                  *
                </label>
              </Row>
              <Row>
                <Radio.Group
                  value={measurementType}
                  onChange={handleMeasurementTypeChange}
                >
                  <Row type="flex" gutter={8}>
                    <Col>
                      <RadioButton value="pre_fixado">
                        {
                          translation.boitelContractsPage.form
                            .dryMatterConsumeContract.preFixed
                        }
                      </RadioButton>
                    </Col>
                    <Col>
                      <RadioButton value="fornercimento">
                        {
                          translation.boitelContractsPage.form
                            .dryMatterConsumeContract.provision
                        }
                      </RadioButton>
                    </Col>
                  </Row>
                </Radio.Group>
              </Row>
            </Col>
            <Col span={12}>
              <Row>
                <label
                  className={
                    errors.dryMatterConsumeContract?.reportType &&
                    submitCount > 0
                      ? "error"
                      : ""
                  }
                >
                  {translation.boitelContractsPage.form.dryMatterConsumeContract
                    .report}
                  *
                </label>
              </Row>
              <Row>
                <Radio.Group value={reportType} onChange={handleReportTypeChange}>
                  <Row type="flex" gutter={8}>
                    <Col>
                      <RadioButton value="cjto">
                        {
                          translation.boitelContractsPage.form
                            .dryMatterConsumeContract.cjto
                        }
                      </RadioButton>
                    </Col>
                    <Col>
                      <RadioButton value="unico">
                        {
                          translation.boitelContractsPage.form
                            .dryMatterConsumeContract.unique
                        }
                      </RadioButton>
                    </Col>
                  </Row>
                </Radio.Group>
              </Row>
            </Col>
          </Row>
        </Col>
        <Col xs={24} md={16} lg={16} xl={12} xxl={12}>
          <Row type="flex" gutter={8} style={{ marginBottom: 8 }}>
            <Col span={2}>
              <label style={{ fontWeight: "bold" }}>
                {translation.boitelContractsPage.form.dryMatterConsumeContract
                  .cjt}
              </label>
            </Col>
            <Col span={6}>
              <label style={{ fontWeight: "bold" }} className={(errors.dryMatterConsumeContract?.herdsWithData?.some(h => h.averageOffer) || errors.dryMatterConsumeContract?.uniqueData?.averageOffer) && submitCount > 0 ? "error" : ""}>
                {
                  translation.boitelContractsPage.form
                    .dryMatterConsumeContract.averageOffer
                }
              </label>
            </Col>
            <Col span={6}>
              <label style={{ fontWeight: "bold" }} className={(errors.dryMatterConsumeContract?.herdsWithData?.some(h => h.numberOfDays) || errors.dryMatterConsumeContract?.uniqueData?.numberOfDays) && submitCount > 0 ? "error" : ""}>
                {
                  translation.boitelContractsPage.form
                    .dryMatterConsumeContract.numberOfDays
                }
              </label>
            </Col>
            <Col span={10}>
              <label style={{ fontWeight: "bold" }} className={(errors.dryMatterConsumeContract?.herdsWithData?.some(h => h.costPerKilogramDryMatter) || errors.dryMatterConsumeContract?.uniqueData?.costPerKilogramDryMatter) && submitCount > 0 ? "error" : ""}>
                {
                  translation.boitelContractsPage.form
                    .dryMatterConsumeContract.value
                }
              </label>
            </Col>
          </Row>
          {(values.herds || []).map((herd, index) => (
            <Row key={herd.id} type="flex" gutter={8} style={{ marginBottom: 8 }}>
              <Col span={2}>
                <Input
                  value={index + 1}
                  disabled
                  style={{ textAlign: "center" }}
                />
              </Col>
              <Col span={6}>
                <NumberFormat
                  disabled={!showAverageOffer}
                  customInput={Input}
                  value={herd.averageOffer || ""}
                  placeholder={
                    translation.boitelContractsPage.form
                      .dryMatterConsumeContract.averageOfferPlaceholder
                  }
                  addonAfter="Kg/d"
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleHerdDataChange(herd.id, "averageOffer", floatValue)
                  }
                  style={{ textAlign: "center" }}
                />
              </Col>
              <Col span={6}>
                  <NumberFormat
                    disabled={!showNumberOfDays}
                    customInput={Input}
                    value={herd.numberOfDays || ""}
                    placeholder={
                      translation.boitelContractsPage.form
                        .dryMatterConsumeContract.numberOfDaysPlaceholder
                    }
                    addonAfter="d"
                    decimalScale={0}
                    decimalSeparator=","
                    thousandSeparator="."
                    onValueChange={({ floatValue }) =>
                      handleHerdDataChange(herd.id, "numberOfDays", floatValue)
                    }
                    style={{ textAlign: "center" }}
                  />
              </Col>
              <Col span={10}>
                  <NumberFormat
                    disabled={!showCostPerKilogramDryMatter}
                    customInput={Input}
                    value={formatCostForDisplay(herd.costPerKilogramDryMatter)}
                    placeholder={
                      translation.boitelContractsPage.form
                        .dryMatterConsumeContract.costPerKilogramDryMatterPlaceholder
                    }
                    min={0}
                    allowLeadingZeros
                    isNumericString
                    addonBefore="R$"
                    addonAfter="/KgMS"
                    decimalScale={2}
                    decimalSeparator=","
                    thousandSeparator="."
                    fixedDecimalScale={true}
                    onValueChange={({ value }) => {
                      handleHerdDataChange(herd.id, "costPerKilogramDryMatter", value || null);
                    }}
                    style={{ textAlign: "center" }}
                  />
              </Col>
            </Row>
          ))}
          {showUniqueRow() && (
            <Row type="flex" gutter={8} style={{ marginTop: 8 }}>
              <Col span={2}>
              </Col>
              <Col span={6}>
                <NumberFormat
                  style={{ textAlign: "center" }}
                  addonAfter="Kg/d"
                  customInput={Input}
                  value={uniqueData.averageOffer || ""}
                  placeholder={
                    translation.boitelContractsPage.form.dryMatterConsumeContract
                      .averageOfferPlaceholder
                  }
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ floatValue }) =>
                    handleUniqueDataChange("averageOffer", floatValue)
                  }
                />
              </Col>
              <Col span={6}>
                <NumberFormat
                  style={{ textAlign: "center" }}
                  disabled={!showUniqueNumberOfDays}
                  addonAfter="d"
                  customInput={Input}
                  value={uniqueData.numberOfDays || ""}
                  placeholder={
                    translation.boitelContractsPage.form.dryMatterConsumeContract
                      .numberOfDaysPlaceholder
                  }
                  decimalScale={0}
                  decimalSeparator=","
                  thousandSeparator="."
                  onValueChange={({ floatValue }) =>
                    handleUniqueDataChange("numberOfDays", floatValue)
                  }
                />
              </Col>
              <Col span={10}>
                <NumberFormat
                  style={{ textAlign: "center" }}
                  addonBefore="R$"
                  addonAfter="/KgMS"
                  customInput={Input}
                  value={formatCostForDisplay(uniqueData.costPerKilogramDryMatter)}
                  placeholder={
                    translation.boitelContractsPage.form.dryMatterConsumeContract
                      .costPerKilogramDryMatterPlaceholder
                  }
                  amin={0}
                  allowLeadingZeros
                  isNumericString
                  decimalScale={2}
                  decimalSeparator=","
                  thousandSeparator="."
                  fixedDecimalScale={true}
                  onValueChange={({ value }) => {
                    handleUniqueDataChange("costPerKilogramDryMatter", value)
                  }}
                />
              </Col>
            </Row>
          )}
        </Col>
      </Row>
    </Card>
  );
};

export default DryMatterConsumeCard;

